import React, { type CSSProperties } from 'react'
import type { SvgIconComponent } from '@mui/icons-material'
import AddShoppingCartOutlinedIcon from '@mui/icons-material/AddShoppingCartOutlined'
import ShoppingBasketOutlinedIcon from '@mui/icons-material/ShoppingBasketOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined'
import PriceCheckOutlinedIcon from '@mui/icons-material/PriceCheckOutlined'
import MoneyOffOutlinedIcon from '@mui/icons-material/MoneyOffOutlined'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined'
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'

type GiaoDienLoai = { accent: string; nen: string; Icon: SvgIconComponent }

const MAC_DINH: GiaoDienLoai = { accent: '#65676B', nen: '#F0F2F5', Icon: NotificationsNoneIcon }

/** Màu + icon theo loại thông báo (vòng tròn trong panel, viền + nền toast). */
const GIAO_DIEN_THEO_LOAI: Record<string, GiaoDienLoai> = {
  CODONHANGMOI: { accent: '#1877F2', nen: '#E8F1FE', Icon: AddShoppingCartOutlinedIcon },
  CODONHANGCANNHAT: { accent: '#8B5CF6', nen: '#F1EBFE', Icon: ShoppingBasketOutlinedIcon },
  CODONHANGCANKIEMTRA: { accent: '#0EA5E9', nen: '#E5F5FD', Icon: FactCheckOutlinedIcon },
  CODONHANGCANDONGGOI: { accent: '#F97316', nen: '#FFF0E4', Icon: Inventory2OutlinedIcon },
  CODONHANGCANDIEUPHOI: { accent: '#6366F1', nen: '#ECEEFE', Icon: AltRouteOutlinedIcon },
  CODONHANGCANGIAO: { accent: '#14B8A6', nen: '#E5F9F5', Icon: LocalShippingOutlinedIcon },
  DONHANG_DUOC_SUA: { accent: '#D97706', nen: '#FEF3C7', Icon: EditNoteOutlinedIcon },
  CANHBAO_DOI_SOAT: { accent: '#D97706', nen: '#FEF3C7', Icon: WarningAmberRoundedIcon },
  GIAO_HANG_THANH_CONG: { accent: '#42B72A', nen: '#E9F8E6', Icon: CheckCircleOutlineIcon },
  GIAO_HANG_THAT_BAI: { accent: '#E4405F', nen: '#FDE8ED', Icon: ErrorOutlineIcon },
  HOA_DON_MOI: { accent: '#1877F2', nen: '#E8F1FE', Icon: ReceiptOutlinedIcon },
  HOA_DON_SUA: { accent: '#D97706', nen: '#FEF3C7', Icon: EditOutlinedIcon },
  HOA_DON_HUY: { accent: '#E4405F', nen: '#FDE8ED', Icon: HighlightOffOutlinedIcon },
  PHIEU_THU_CHI_CHO_DUYET: { accent: '#EAB308', nen: '#FEF9C3', Icon: PendingActionsOutlinedIcon },
  PHIEU_THU_CHI_DA_DUYET: { accent: '#16A34A', nen: '#E8F8EF', Icon: PriceCheckOutlinedIcon },
  PHIEU_THU_CHI_TU_CHOI: { accent: '#E4405F', nen: '#FDE8ED', Icon: MoneyOffOutlinedIcon },
  YCX_CAN_XU_LY: { accent: '#0EA5E9', nen: '#E5F5FD', Icon: ReceiptLongOutlinedIcon },
  CHUYEN_HANG_DEN: { accent: '#6366F1', nen: '#ECEEFE', Icon: SwapHorizOutlinedIcon },
}

export const giaoDienLoai = (loai: string): GiaoDienLoai => GIAO_DIEN_THEO_LOAI[loai] ?? MAC_DINH

export const thongBaoAccent = (loai: string): string => giaoDienLoai(loai).accent

/** Vòng tròn icon màu theo loại. */
export const ThongBaoLoaiAvatar: React.FC<{ loai: string; size?: number }> = ({ loai, size = 40 }) => {
  const { accent, Icon } = giaoDienLoai(loai)
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: accent,
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icon sx={{ fontSize: Math.round(size * 0.55) }} />
    </span>
  )
}

/** Toast realtime — nền pastel + viền trái theo loại (AlertSnackbar đọc --tb-accent cho icon). */
export const thongBaoToastStyle = (loai: string): CSSProperties => {
  const { accent, nen } = giaoDienLoai(loai)
  return {
    backgroundColor: nen,
    color: '#1C1E21',
    borderLeft: `4px solid ${accent}`,
    maxWidth: 380,
    ['--tb-accent' as string]: accent,
  }
}

/** "Vừa xong" / "5 phút" / "3 giờ" / "2 ngày" / ngày đầy đủ khi quá 1 tuần. */
export const thoiGianTuongDoi = (value: string | null | undefined, locale = 'vi'): string => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const vi = locale.startsWith('vi')
  const zh = locale.startsWith('zh')
  const giay = Math.floor((Date.now() - date.getTime()) / 1000)
  if (giay < 45) return vi ? 'Vừa xong' : zh ? '刚刚' : 'Just now'
  const phut = Math.floor(giay / 60)
  if (phut < 60) return vi ? `${phut} phút trước` : zh ? `${phut}分钟前` : `${phut}m ago`
  const gio = Math.floor(phut / 60)
  if (gio < 24) return vi ? `${gio} giờ trước` : zh ? `${gio}小时前` : `${gio}h ago`
  const ngay = Math.floor(gio / 24)
  if (ngay < 7) return vi ? `${ngay} ngày trước` : zh ? `${ngay}天前` : `${ngay}d ago`
  return date.toLocaleString(vi ? 'vi-VN' : zh ? 'zh-CN' : 'en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
